/**
 * Fullscreen takeover helpers — wave 0 of the Total-Redesign-Spec §1b.
 *
 * Desktop / Android: real Fullscreen API on the user's Ready/Start CLICK
 * (transient activation required), plus screen.orientation.lock for
 * landscape-locked games (only valid while fullscreen).
 *
 * iPhone Safari: still no element Fullscreen API (2026) — every call here
 * silently no-ops; the PWA manifest + Add-to-Home-Screen nudge is the iOS
 * path to edge-to-edge play.
 */

type OrientationPref = 'landscape' | 'portrait';

/** Narrow `screen.orientation.lock/unlock` — TypeScript's DOM lib dropped
 *  `lock()` (it's not universal); feature-detect at runtime instead. */
interface LockableScreenOrientation {
  lock?: (orientation: string) => Promise<void>;
  unlock?: () => void;
}

export function isIos(): boolean {
  const ua = navigator.userAgent;
  // iPadOS 13+ masquerades as macOS but exposes touch points.
  return /iPhone|iPad|iPod/.test(ua)
    || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

/** True when the Fullscreen API is actually usable here. */
export function fullscreenSupported(): boolean {
  return typeof document.documentElement.requestFullscreen === 'function'
    && document.fullscreenEnabled !== false;
}

/**
 * Enter fullscreen takeover. MUST be called from inside a user-gesture
 * handler (the Ready/Start click) — browsers reject it otherwise.
 * Optionally locks orientation for landscape-declared games (lock is only
 * valid while fullscreen, so it runs after the request resolves).
 */
export async function enterGameFullscreen(orientation?: OrientationPref | null): Promise<void> {
  const el = document.documentElement;
  if (typeof el.requestFullscreen === 'function') {
    try {
      await el.requestFullscreen({ navigationUI: 'hide' });
    } catch {
      // iOS Safari (unsupported), permission denied, or not in a gesture —
      // all fine; the page keeps working in-tab.
    }
  }
  if (orientation === 'landscape') {
    await lockOrientation('landscape');
  }
}

/** Attempt an orientation lock. Only meaningful while fullscreen; no-ops
 *  everywhere it isn't supported (iOS: never supported). */
export async function lockOrientation(orientation: OrientationPref): Promise<void> {
  if (!document.fullscreenElement) return;
  try {
    const so = screen.orientation as unknown as LockableScreenOrientation;
    await so.lock?.(orientation);
  } catch {
    // Desktop browsers throw NotSupportedError — expected.
  }
}

/** Unlock orientation + leave fullscreen (round end / leaving play). */
export async function exitGameFullscreen(): Promise<void> {
  try {
    const so = screen.orientation as unknown as LockableScreenOrientation;
    so.unlock?.();
  } catch { /* */ }
  if (document.fullscreenElement) {
    try {
      await document.exitFullscreen();
    } catch { /* */ }
  }
}

/** Subscribe to fullscreen enter/exit. Returns an unsubscribe fn. */
export function onFullscreenChange(cb: () => void): () => void {
  document.addEventListener('fullscreenchange', cb);
  return () => document.removeEventListener('fullscreenchange', cb);
}

/** Is the document currently in fullscreen takeover? */
export function isFullscreen(): boolean {
  return !!document.fullscreenElement;
}
